"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WelcomeToOurHive() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: underlineRef,
    isVisible: underlineVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.3
  });
  return <section data-section-id="1297" id="welcome-to-our-hive" className="relative overflow-hidden bg-background py-28 md:py-40 border-b border-border">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10">
        {}
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {}
            <div className="font-mono uppercase text-[0.8125rem] font-medium tracking-[0.18em] text-muted-foreground">WELCOME TO CLASS 2A</div>

            {}
            <div className="mt-4 flex items-center gap-2">
              <div className="h-px flex-1 bg-foreground" />
              <div className="size-2 rounded-lg bg-primary" />
            </div>

            {}
            <h1 className="mt-8 font-default font-bold text-foreground" style={{
            fontSize: "clamp(2.75rem, 7.2vw, 6.5rem)",
            letterSpacing: "-0.02em",
            lineHeight: "0.96"
          }}>
              Learning is a{" "}
              <span className="relative inline-block italic">
                bright adventure
                <span ref={underlineRef} aria-hidden="true" className="absolute left-0 bottom-1 h-[2px] bg-primary transition-all ease-out" style={{
                width: underlineVisible ? "100%" : "0%",
                transitionDuration: "700ms",
                transitionDelay: "200ms",
                transitionTimingFunction: "cubic-bezier(0.65, 0, 0.35, 1)"
              }} />
              </span>{" "}
              every day.
            </h1>

            {}
            <p className="mt-8 font-default text-foreground/85 max-w-[46ch]" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
            lineHeight: "1.6"
          }}>
              Welcome to Class 2A, where curiosity meets creativity. From our cozy reading corners to our vibrant art walls, we build a foundation of kindness and discovery. Join us as we explore new ideas, celebrate our unique talents, and grow together in our sunny classroom home.
            </p>

            {}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/Class2AHubPage">
                <Button size="lg" className="group bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold tracking-[0.05em] text-[0.8125rem] px-7 py-6 rounded-lg shadow-md transition-all duration-200">
                  Explore Our Hub
                  <ArrowRight className="transition-transform duration-200 group-hover:translate-x-1.5" />
                </Button>
              </Link>
              <Link to="/ParentToolboxPage">
                <Button size="lg" variant="outline" className="group bg-transparent border-2 border-secondary text-secondary hover:bg-muted font-default font-semibold tracking-[0.05em] text-[0.8125rem] px-7 py-6 rounded-lg shadow-sm transition-all duration-200">
                  Parent Toolbox
                  <ArrowRight className="transition-transform duration-200 group-hover:translate-x-1.5" />
                </Button>
              </Link>
            </div>

            {}
            <div className="mt-8 font-mono text-[0.75rem] tracking-[0.1em] text-muted-foreground uppercase">
              Room 2A — Primary School Campus
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 relative transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: "120ms"
        }}>
            <div className="relative h-[320px] md:h-[480px] lg:h-[560px] w-full">
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_adventure_scene.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_adventure_scene.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_adventure_scene.png-wvc-sizes" alt="A whimsical illustration of a primary school classroom with colorful art supplies, a cozy reading nook, and a bright sun shining through the window" loading="lazy" decoding="async" className="absolute inset-0 size-full object-contain" />

              {}
              <div className="absolute -top-4 -right-2 md:top-0 md:right-0 group">
                <div className="relative size-[120px] md:size-[150px] transition-transform duration-700 ease-in-out group-hover:-rotate-6" style={{
                transitionTimingFunction: "ease-in-out"
              }}>
                  <svg viewBox="0 0 200 200" className="size-full" aria-hidden="true">
                    <defs>
                      <path id="stamp-top-arc" d="M 30,100 A 70,70 0 0 1 170,100" fill="none" />
                      <path id="stamp-bottom-arc" d="M 30,100 A 70,70 0 0 0 170,100" fill="none" />
                    </defs>
                    <circle cx="100" cy="100" r="88" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-foreground" />
                    <circle cx="100" cy="100" r="78" fill="none" stroke="currentColor" strokeWidth="1" className="text-foreground" />
                    <text className="text-foreground font-mono" fill="currentColor" style={{
                    fontSize: "11px",
                    letterSpacing: "0.18em"
                  }}>
                      <textPath href="#stamp-top-arc" startOffset="50%" textAnchor="middle">
                        CLASS 2A · LEARNING
                      </textPath>
                    </text>
                    <text className="text-foreground font-mono" fill="currentColor" style={{
                    fontSize: "11px",
                    letterSpacing: "0.18em"
                  }}>
                      <textPath href="#stamp-bottom-arc" startOffset="50%" textAnchor="middle">
                        · GROWING TOGETHER ·
                      </textPath>
                    </text>
                    {}
                    <g className="text-primary" fill="currentColor">
                      <circle cx="100" cy="105" r="14" />
                      <path d="M 100,91 Q 95,80 88,78 Q 92,86 100,91 Z" className="text-foreground" fill="currentColor" />
                    </g>
                    <text x="100" y="135" textAnchor="middle" className="font-serif italic text-foreground" fill="currentColor" style={{
                    fontSize: "10px"
                  }}>
                      EST. 2024
                    </text>
                  </svg>
                </div>
              </div>

              {}
              <div className="absolute bottom-2 left-2 font-mono text-[0.7rem] tracking-[0.1em] text-muted-foreground uppercase">
                Creativity ↔ Discovery
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}